import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Stack,
  Alert,
  TextField,
  Button,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { useSettings } from '../../contexts/SettingsContext';
import { auth } from '../../services/api';
import ResendVerification from '../auth/ResendVerification';
import VerifiedIcon from '@mui/icons-material/Verified';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface UserData {
  email: string;
  isVerified: boolean;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordValidation {
  hasMinLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  matches: boolean;
}

const Settings: React.FC = () => {
  const { darkMode, toggleDarkMode } = useSettings();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordStatus, setPasswordStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [validation, setValidation] = useState<PasswordValidation>({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    matches: false,
  });

  const validatePassword = (password: string, confirmPassword: string) => {
    setValidation({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&]/.test(password),
      matches: password === confirmPassword && password !== '',
    });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await auth.getUser();
        setUserData({
          email: response.email,
          isVerified: response.isVerified,
        });
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };

    fetchUserData();
  }, []);

  const handlePasswordChange = (field: keyof PasswordData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPasswordData = {
      ...passwordData,
      [field]: e.target.value,
    };
    setPasswordData(newPasswordData);

    // Reset status when user starts typing again
    if (passwordStatus !== 'idle') {
      setPasswordStatus('idle');
      setPasswordMessage('');
    }

    // Validate password in real-time
    if (field === 'newPassword' || field === 'confirmPassword') {
      validatePassword(
        field === 'newPassword' ? e.target.value : newPasswordData.newPassword,
        field === 'confirmPassword' ? e.target.value : newPasswordData.confirmPassword
      );
    }
  };

  const isPasswordValid = () => {
    return Object.values(validation).every(value => value === true);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPasswordValid()) {
      setPasswordStatus('error');
      setPasswordMessage('Please ensure all password requirements are met');
      return;
    }

    setPasswordStatus('loading');

    try {
      await auth.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      setPasswordStatus('success');
      setPasswordMessage('Password updated successfully');
      // Clear form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      // Reset validation
      validatePassword('', '');
    } catch (error: any) {
      setPasswordStatus('error');
      setPasswordMessage(error.message || 'Failed to update password');
    }
  };

  const renderValidationStatus = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Password Requirements:
      </Typography>
      <Stack spacing={1}>
        <FormHelperText error={!validation.hasMinLength}>
          ✓ At least 8 characters
        </FormHelperText>
        <FormHelperText error={!validation.hasUpperCase}>
          ✓ At least one uppercase letter
        </FormHelperText>
        <FormHelperText error={!validation.hasLowerCase}>
          ✓ At least one lowercase letter
        </FormHelperText>
        <FormHelperText error={!validation.hasNumber}>
          ✓ At least one number
        </FormHelperText>
        <FormHelperText error={!validation.hasSpecialChar}>
          ✓ At least one special character (@$!%*?&)
        </FormHelperText>
        <FormHelperText error={!validation.matches}>
          ✓ Passwords match
        </FormHelperText>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      {/* Email Verification Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Account Settings
          </Typography>
          <List sx={{ width: '100%' }}>
            <ListItem
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
                gap: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1">Email Verification</Typography>
                  {userData?.isVerified ? (
                    <Chip
                      icon={<VerifiedIcon />}
                      label="Verified"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<ErrorOutlineIcon />}
                      label="Not Verified"
                      color="warning"
                      size="small"
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {userData?.email}
                </Typography>
              </Stack>

              {!userData?.isVerified && userData?.email && (
                <Box sx={{ minWidth: 200 }}>
                  <ResendVerification
                    email={userData.email}
                    onSuccess={() => {
                      setUserData(prev => prev ? { ...prev, isVerified: true } : null);
                    }}
                  />
                </Box>
              )}
            </ListItem>
            <Divider />

            {/* Password Change Section */}
            <ListItem sx={{ display: 'block', py: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Change Password
              </Typography>
              <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 2 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="currentPassword"
                  label="Current Password"
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange('currentPassword')}
                  disabled={passwordStatus === 'loading'}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label="New Password"
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange('newPassword')}
                  disabled={passwordStatus === 'loading'}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange('confirmPassword')}
                  disabled={passwordStatus === 'loading'}
                />

                {renderValidationStatus()}

                {(passwordStatus === 'success' || passwordStatus === 'error') && (
                  <Alert 
                    severity={passwordStatus === 'success' ? 'success' : 'error'}
                    sx={{ mt: 2 }}
                  >
                    {passwordMessage}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  sx={{ mt: 2 }}
                  disabled={
                    passwordStatus === 'loading' ||
                    !passwordData.currentPassword ||
                    !isPasswordValid()
                  }
                >
                  {passwordStatus === 'loading' ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </Box>
            </ListItem>
            <Divider />
          </List>
        </CardContent>
      </Card>

      {/* Appearance Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Appearance
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Dark Mode" />
              <ListItemSecondaryAction>
                <Switch
                  edge="end"
                  checked={darkMode}
                  onChange={toggleDarkMode}
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings; 